import React, { useState, useMemo } from 'react';
import { Language } from '../../types/kundali';
import {
  ClassicalRuleEvidence,
  ClassicalShastra,
  ShastraEvaluatedStatus,
  ShastraVerificationStatus,
  V519PresentationMode
} from '../../types/v519';
import {
  CLASSICAL_EVIDENCE_REGISTRY,
  searchEvidence
} from '../../utils/v519/classicalEvidenceEngine';
import {
  BookOpen,
  X,
  Search,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  Filter,
  Layers,
  Sparkles,
  ExternalLink,
  ChevronRight,
  Info
} from 'lucide-react';

export interface ClassicalEvidenceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  evidenceList?: readonly ClassicalRuleEvidence[];
  selectedRuleId?: string;
  lang: Language;
  presentationMode?: V519PresentationMode;
  initialShastraFilter?: ClassicalShastra;
}

const SHASTRA_NAMES: Record<ClassicalShastra, { en: string; hi: string; author: string }> = {
  BPHS: {
    en: 'Brihat Parashara Hora Shastra',
    hi: 'बृहत्पाराशर होराशास्त्रम्',
    author: 'Maharshi Parashara'
  },
  PHALADEEPIKA: {
    en: 'Phaladeepika',
    hi: 'फलदीपिका',
    author: 'Mantreswara'
  },
  JATAKA_PARIJATA: {
    en: 'Jataka Parijata',
    hi: 'जातक पारिजात',
    author: 'Vaidyanatha Dikshita'
  },
  SARAVALI: {
    en: 'Saravali',
    hi: 'सारावली',
    author: 'Kalyanavarma'
  },
  UTTARA_KALAMRITA: {
    en: 'Uttara Kalamrita',
    hi: 'उत्तर कालामृत',
    author: 'Kalidasa'
  }
};

const STATUS_BADGES: Record<ShastraEvaluatedStatus, { bg: string; text: string; labelEn: string; labelHi: string }> = {
  APPLIED: {
    bg: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    text: 'text-emerald-800',
    labelEn: 'Applied in Kundali',
    labelHi: 'कुंडली में पूर्णतः सक्रिय'
  },
  PARTIALLY_ACTIVE: {
    bg: 'bg-teal-100 text-teal-900 border-teal-300',
    text: 'text-teal-800',
    labelEn: 'Partially Active',
    labelHi: 'आंशिक सक्रिय'
  },
  NOT_MET: {
    bg: 'bg-astro-secondary-surface text-astro-secondary-text border-astro-border',
    text: 'text-astro-secondary-text',
    labelEn: 'Condition Not Met',
    labelHi: 'शर्त अनुपस्थित'
  },
  NOT_EVALUATED: {
    bg: 'bg-amber-100 text-amber-900 border-amber-300',
    text: 'text-amber-800',
    labelEn: 'Not Evaluated',
    labelHi: 'अमूल्यांकित'
  }
};

export const ClassicalEvidenceDrawer: React.FC<ClassicalEvidenceDrawerProps> = ({
  isOpen,
  onClose,
  evidenceList,
  selectedRuleId,
  lang,
  presentationMode = 'PRO',
  initialShastraFilter
}) => {
  const isHi = lang === 'hi';
  const isPro = presentationMode === 'PRO';

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedShastra, setSelectedShastra] = useState<ClassicalShastra | 'ALL'>(initialShastraFilter || 'ALL');
  const [selectedStatus, setSelectedStatus] = useState<ShastraEvaluatedStatus | 'ALL'>('ALL');
  const [activeRuleId, setActiveRuleId] = useState<string | undefined>(selectedRuleId);

  // Use provided list or whole registry
  const baseRules = useMemo(() => {
    if (evidenceList && evidenceList.length > 0) {
      return evidenceList;
    }
    return Object.values(CLASSICAL_EVIDENCE_REGISTRY);
  }, [evidenceList]);

  // Filter and search
  const filteredRules = useMemo(() => {
    let result = baseRules;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(r =>
        r.sanskritSutra.toLowerCase().includes(q) ||
        r.englishTranslation.toLowerCase().includes(q) ||
        r.hindiTranslation.toLowerCase().includes(q) ||
        r.astrologicalCondition.toLowerCase().includes(q) ||
        r.ruleId.toLowerCase().includes(q) ||
        `${r.chapter}`.includes(q)
      );
    }

    if (selectedShastra !== 'ALL') {
      result = result.filter(r => r.shastra === selectedShastra);
    }

    if (selectedStatus !== 'ALL') {
      result = result.filter(r => r.evaluatedStatus === selectedStatus);
    }

    return result;
  }, [baseRules, searchQuery, selectedShastra, selectedStatus]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex justify-end animate-fadeIn">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over Drawer Panel */}
      <div
        id="classical-evidence-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="classical-drawer-title"
        className="relative w-full max-w-2xl bg-astro-bg border-l border-astro-border shadow-2xl h-full flex flex-col z-10 text-astro-text"
      >
        {/* Drawer Header */}
        <div className="p-4 sm:p-5 bg-astro-primary text-white flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 text-astro-accent flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h3 id="classical-drawer-title" className="text-base sm:text-lg font-bold font-serif">
                {isHi ? 'शास्त्रीय प्रमाण एवं प्रामाणिक श्लोक कोष' : 'Classical Shastra Evidence Registry'}
              </h3>
              <p className="text-xs text-astro-muted">
                {isHi
                  ? 'महर्षि पराशर, मन्त्रेश्वर एवं कल्याणवर्मा के मूल संस्कृत सूत्र'
                  : 'Canonical Sanskrit Sutras with Academic Translations & Verse Citations'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            aria-label="Close drawer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search & Filter Strip */}
        <div className="p-4 border-b border-astro-border bg-astro-card space-y-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="w-4 h-4 text-astro-muted absolute left-3 top-3" />
            <input
              type="text"
              placeholder={isHi ? 'संस्कृत श्लोक, अनुवाद, अध्याय या नियम खोजें...' : 'Search sutras, translations, chapter or keywords...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-astro-border rounded-xl text-xs text-astro-text placeholder-stone-400 focus:outline-none focus:ring-1 focus:ring-astro-primary"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5 text-astro-muted hover:text-astro-secondary-text cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
            <span className="text-[11px] font-bold text-astro-secondary-text shrink-0">
              {isHi ? 'ग्रंथ:' : 'Shastra:'}
            </span>
            <button
              onClick={() => setSelectedShastra('ALL')}
              className={`px-2.5 py-1 rounded-lg font-medium whitespace-nowrap transition-colors cursor-pointer ${
                selectedShastra === 'ALL'
                  ? 'bg-astro-primary text-white font-bold'
                  : 'bg-white border border-astro-border text-astro-text hover:border-astro-accent'
              }`}
            >
              {isHi ? 'सभी ग्रंथ' : 'All Shastras'}
            </button>
            {(Object.keys(SHASTRA_NAMES) as ClassicalShastra[]).map((s) => (
              <button
                key={s}
                onClick={() => setSelectedShastra(s)}
                className={`px-2.5 py-1 rounded-lg font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  selectedShastra === s
                    ? 'bg-astro-primary text-white font-bold'
                    : 'bg-white border border-astro-border text-astro-text hover:border-astro-accent'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Rules Scrollable Container */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          <div className="flex items-center justify-between text-xs text-astro-secondary-text">
            <span>
              {isHi
                ? `${filteredRules.length} शास्त्रीय सूत्र उपलब्ध`
                : `Showing ${filteredRules.length} classical citations`}
            </span>
            <span className="flex items-center gap-1 text-emerald-800 font-bold">
              <ShieldCheck className="w-3.5 h-3.5" />
              {isHi ? '१००% सत्यापित ग्रंथ उद्धरण' : '100% Verified Canonical Rules'}
            </span>
          </div>

          {filteredRules.length === 0 ? (
            <div className="p-8 text-center bg-white rounded-xl border border-astro-border text-xs text-astro-secondary-text">
              {isHi
                ? 'कोई शास्त्रीय प्रमाण नहीं मिला। कृपया अपने खोज शब्द बदलें।'
                : 'No classical sutras matched your query. Try clearing filters.'}
            </div>
          ) : (
            filteredRules.map((rule) => {
              const shastraMeta = SHASTRA_NAMES[rule.shastra] || SHASTRA_NAMES.BPHS;
              const statusMeta = STATUS_BADGES[rule.evaluatedStatus] || STATUS_BADGES.APPLIED;
              const isVerified = rule.verificationStatus === 'VERIFIED';

              return (
                <article
                  key={rule.ruleId}
                  id={`classical-rule-${rule.ruleId}`}
                  className="bg-white border border-astro-border rounded-xl p-4 sm:p-5 space-y-3.5 hover:border-astro-accent transition-all shadow-2xs"
                >
                  {/* Rule Header: Shastra, Chapter, Verse & Status */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border pb-2.5">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm text-astro-primary font-serif">
                          {isHi ? shastraMeta.hi : shastraMeta.en}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-astro-card text-astro-accent border border-astro-border">
                          Ch. {rule.chapter}, Verse {rule.verse}
                        </span>
                      </div>
                      <div className="text-[11px] text-astro-secondary-text mt-0.5">
                        {isHi ? `रचयिता: ${shastraMeta.author}` : `Author: ${shastraMeta.author}`} • {rule.sourceEdition}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-center">
                      <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold border ${statusMeta.bg}`}>
                        {isHi ? statusMeta.labelHi : statusMeta.labelEn}
                      </span>
                    </div>
                  </div>

                  {/* Devanagari Sanskrit Sutra (Authentic & Preserved) */}
                  <div className="p-3 bg-astro-bg rounded-lg border border-astro-border border-l-4 border-l-[#B58A4A] space-y-1">
                    <div className="text-[10px] uppercase font-bold text-astro-accent tracking-wider">
                      {isHi ? 'मूल संस्कृत सूत्र' : 'Canonical Sanskrit Sutra'}
                    </div>
                    <p className="font-serif text-sm sm:text-base text-astro-primary leading-relaxed font-semibold">
                      {rule.sanskritSutra}
                    </p>
                  </div>

                  {/* Translations */}
                  <div className="space-y-2 text-xs">
                    {/* Hindi Translation */}
                    <div className="space-y-0.5">
                      <span className="font-bold text-astro-secondary-text text-[11px]">
                        {isHi ? 'शास्त्रीय हिन्दी अनुवाद:' : 'Authentic Hindi Translation:'}
                      </span>
                      <p className="text-astro-text leading-relaxed">
                        {rule.hindiTranslation}
                      </p>
                    </div>

                    {/* English Translation */}
                    <div className="space-y-0.5">
                      <span className="font-bold text-astro-secondary-text text-[11px]">
                        {isHi ? 'विद्वत अंग्रेजी अनुवाद:' : 'Scholarly English Translation:'}
                      </span>
                      <p className="text-astro-text leading-relaxed">
                        {rule.englishTranslation}
                      </p>
                    </div>
                  </div>

                  {/* Astrological Condition Description */}
                  <div className="p-2.5 rounded-lg bg-astro-card text-xs space-y-1 border border-astro-border">
                    <div className="font-bold text-astro-primary flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 text-astro-accent" />
                      <span>{isHi ? 'ज्योतिषीय योग एवं शर्त' : 'Astrological Condition Evaluated'}</span>
                    </div>
                    <p className="text-astro-secondary-text leading-snug">
                      {rule.astrologicalCondition}
                    </p>
                  </div>

                  {/* Verification Footer */}
                  <div className="flex items-center justify-between text-[11px] text-astro-secondary-text pt-1">
                    <span>
                      {isHi ? 'नियम आईडी:' : 'Rule ID:'} <code className="font-mono text-astro-primary">{rule.ruleId}</code>
                    </span>
                    <span className="flex items-center gap-1 font-bold text-emerald-800">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      {isHi ? 'प्रमाणित स्रोत' : 'Canonical Verified'}
                    </span>
                  </div>
                </article>
              );
            })
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-4 bg-astro-card border-t border-astro-border flex items-center justify-between text-xs">
          <span className="text-astro-secondary-text">
            {isHi ? 'वैदिक ज्योतिष प्रामाणिक संदर्भ' : 'Canonical Vedic Shastra Reference'}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-astro-primary hover:bg-[#123D3C] text-white font-bold transition-colors cursor-pointer"
          >
            {isHi ? 'बंद करें' : 'Close Evidence'}
          </button>
        </div>
      </div>
    </div>
  );
};
